/// RightSidebar: Resizable right panel for Properties editing.
/// Collapses to a thin strip with expand button when hidden.

import { useCallback, useRef } from "react";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useUIStore } from "@/stores/uiStore";
import { PropertiesPanel } from "@/components/panels/PropertiesPanel";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RightSidebar() {
  const visible = useUIStore((s) => s.rightPanelVisible);
  const width = useUIStore((s) => s.rightPanelWidth);
  const setWidth = useUIStore((s) => s.setRightPanelWidth);
  const toggle = useUIStore((s) => s.toggleRightPanel);

  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Resize via pointer drag on left edge
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: width };
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
    },
    [width],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      // Dragging left edge: moving left increases width
      const delta = dragRef.current.startX - e.clientX;
      setWidth(dragRef.current.startWidth + delta);
    },
    [setWidth],
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  if (!visible) {
    return (
      <button
        type="button"
        className="flex h-full w-8 items-center justify-center border-l border-zinc-700 bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
        onClick={toggle}
        aria-label="Open right sidebar"
      >
        <PanelRightOpen className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      data-testid="right-sidebar"
      className="relative flex flex-col border-l border-zinc-700 bg-zinc-800 min-h-0 select-none"
      style={{ width: `${width}px`, minWidth: "200px", maxWidth: "600px" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-700 px-3 py-1">
        <span className="text-xs font-semibold text-zinc-300">Properties</span>
        <button
          type="button"
          className="rounded p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
          onClick={toggle}
          aria-label="Collapse right sidebar"
        >
          <PanelRightClose className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Properties panel content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <PropertiesPanel />
      </div>

      {/* Left-edge resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/30 active:bg-blue-500/50 z-10"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
    </div>
  );
}
